import type { ReactNode, MouseEvent } from 'react';
import { Form } from 'react-bootstrap';
import { HiChevronUp, HiChevronDown, HiChevronUpDown } from 'react-icons/hi2';
import {
  TableRoot,
  TableScroll,
  Table,
  Thead,
  Tbody,
  EmptyRow,
  SortableHeader,
  SelectCell,
} from './DataTable.styled';

export type ColumnAlign = 'left' | 'right' | 'center';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: ColumnAlign;
  width?: string;
  className?: string;
  /** Set a sort key to make this column sortable. Caller controls sort state. */
  sortKey?: string;
}

export interface SortState {
  field: string;
  order: 'asc' | 'desc';
}

export interface BulkSelectState<T> {
  /** Set of row keys currently selected. */
  selectedKeys: Set<string>;
  onToggle: (row: T) => void;
  onToggleAll: () => void;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T, event: MouseEvent<HTMLTableRowElement>) => void;
  rowClassName?: (row: T) => string | undefined;
  emptyMessage?: ReactNode;
  ariaLabel?: string;
  /** Pass a sort state + onSortChange handler to enable column sorting. */
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  /** Pass a bulk-select state to render a checkbox column. */
  bulkSelect?: BulkSelectState<T>;
}

const alignClass = (a?: ColumnAlign) =>
  a === 'right' ? 'is-numeric' : a === 'center' ? 'is-center' : undefined;

function SortIcon({ field, sortKey, order }: { field?: string; sortKey: string; order?: 'asc' | 'desc' }) {
  if (field !== sortKey) return <HiChevronUpDown size="0.75rem" />;
  return order === 'asc' ? <HiChevronUp size="0.75rem" /> : <HiChevronDown size="0.75rem" />;
}

export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  emptyMessage,
  ariaLabel,
  sort,
  onSortChange,
  bulkSelect,
}: DataTableProps<T>) {
  if (rows.length === 0 && emptyMessage) {
    return (
      <TableRoot>
        <EmptyRow>{emptyMessage}</EmptyRow>
      </TableRoot>
    );
  }

  const allSelected =
    !!bulkSelect && rows.length > 0 && rows.every((r) => bulkSelect.selectedKeys.has(rowKey(r)));
  const someSelected =
    !!bulkSelect && !allSelected && rows.some((r) => bulkSelect.selectedKeys.has(rowKey(r)));

  const handleHeaderClick = (col: DataTableColumn<T>) => {
    if (!col.sortKey || !onSortChange) return;
    if (sort?.field === col.sortKey) {
      onSortChange({ field: col.sortKey, order: sort.order === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ field: col.sortKey, order: 'asc' });
    }
  };

  return (
    <TableRoot>
      <TableScroll>
        <Table aria-label={ariaLabel}>
          <Thead>
            <tr>
              {bulkSelect && (
                <SelectCell as="th" scope="col">
                  <Form.Check
                    type="checkbox"
                    checked={allSelected}
                    ref={(el: HTMLInputElement | null) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={bulkSelect.onToggleAll}
                    aria-label="Select all"
                  />
                </SelectCell>
              )}
              {columns.map((col) => {
                const isSortable = !!col.sortKey;
                return (
                  <th
                    key={col.key}
                    className={[alignClass(col.align), col.className].filter(Boolean).join(' ') || undefined}
                    style={col.width ? { width: col.width } : undefined}
                    scope="col"
                  >
                    {isSortable ? (
                      <SortableHeader type="button" onClick={() => handleHeaderClick(col)}>
                        {col.header}
                        <SortIcon field={sort?.field} sortKey={col.sortKey!} order={sort?.order} />
                      </SortableHeader>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </Thead>
          <Tbody>
            {rows.map((row) => {
              const key = rowKey(row);
              const isSelected = bulkSelect?.selectedKeys.has(key) ?? false;
              return (
                <tr
                  key={key}
                  className={
                    [
                      onRowClick ? 'is-clickable' : undefined,
                      isSelected ? 'is-selected' : undefined,
                      rowClassName?.(row),
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  onClick={onRowClick ? (e) => onRowClick(row, e) : undefined}
                >
                  {bulkSelect && (
                    <SelectCell onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => bulkSelect.onToggle(row)}
                        aria-label="Select row"
                      />
                    </SelectCell>
                  )}
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={[alignClass(col.align), col.className].filter(Boolean).join(' ') || undefined}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </Tbody>
        </Table>
      </TableScroll>
    </TableRoot>
  );
}
